import { type DatetimeType } from '@common/services/datetime';
import { uniq } from 'lodash';

import { parseHourEntryDates, parseInTz } from './dates';
import { HourEntryFragment } from '@common/graphql/schema';

export const formatOrganizationProfilesEEIDs = (
  organizationProfiles: { employeeId?: string | null }[]
): string | null | undefined => {
  return organizationProfiles.find((orgData) => !!orgData?.employeeId)
    ?.employeeId;
};

export const formatHourEntryTimes = (entry: HourEntryFragment | undefined) => {
  if (!entry) {
    return '';
  }

  const parsed = parseHourEntryDates(entry);
  const assumedTZ = parsed[0].format('  z');

  return parsed.map((date) => date.format('HH:mm')).join(' - ') + assumedTZ;
};

export const formatLabelDate = (date: DatetimeType) =>
  date.format('ddd, MMM D');

export const formatSummaryDate = (date: DatetimeType) =>
  date.format('dddd, MMM D');

export const formatDateInTz = (date: string, dateTz?: string | null) =>
  parseInTz(date, dateTz).format('MM/DD/YYYY  HH:mm');

export const formatZoneAbbreviation = (date: string, dateTz?: string | null) =>
  parseInTz(date, dateTz).format('z');

export const formatDateWithTz = (
  date: DatetimeType | undefined,
  timeZone: string
) => {
  if (!date) {
    return '';
  }

  const parsed = date.tz(timeZone);
  const assumedTZ = parsed.format('z');

  return parsed.format('M/D/YY HH:mm ') + assumedTZ;
};

export const formatEntryDates = (hourEntry: HourEntryFragment) => {
  const startedAt = parseInTz(hourEntry.startedAt!, hourEntry.startedAtTz);
  const endedAt = parseInTz(hourEntry.endedAt!, hourEntry.endedAtTz);

  if (startedAt.isSame(endedAt, 'day')) {
    return startedAt.format('M/D');
  }

  return `${startedAt.format('M/D')} - ${endedAt.format('M/D')}`;
};

export const formatTimesheetDates = (
  startDate: string | undefined,
  endDate: string | undefined,
  timeZone: string | undefined,
  completeDate?: boolean
): string => {
  if (!startDate || !endDate) {
    return '';
  }

  const formattedStartDate = completeDate
    ? formatSummaryDate(parseInTz(startDate, timeZone))
    : formatLabelDate(parseInTz(startDate, timeZone));
  const formattedEndDate = completeDate
    ? formatSummaryDate(parseInTz(endDate, timeZone))
    : formatLabelDate(parseInTz(endDate, timeZone));

  return `${formattedStartDate} - ${formattedEndDate}`;
};

export const formatSecondsToDuration = (totalSeconds: number): string => {
  const totalMinutes = Math.floor(Math.abs(totalSeconds) / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const prefix = totalSeconds < 0 ? '-' : '';

  if (minutes === 0) {
    return `${prefix}${hours}h`;
  } else if (hours === 0) {
    return `${prefix}${minutes}m`;
  } else {
    return `${prefix}${hours}h ${minutes}m`;
  }
};

export const formatHiringLeaders = (hiringLeaders: { name: string }[]) => {
  return uniq(hiringLeaders)
    .map((leader) => leader.name)
    .sort((a, b) => a.localeCompare(b))
    .join(', ');
};

type Instant = { valueOf(): number };

const secondsBetween = (start: Instant, end: Instant) =>
  Math.trunc((end.valueOf() - start.valueOf()) / 1000);

export const calculateTotalSeconds = (
  clockIn: DatetimeType | undefined,
  clockOut: DatetimeType | undefined,
  breakStart: DatetimeType | undefined,
  breakEnd: DatetimeType | undefined
) => {
  const totalSecondsClocks =
    clockIn && clockOut ? secondsBetween(clockIn, clockOut) : 0;
  const totalSecondsBreaks =
    breakStart && breakEnd ? secondsBetween(breakStart, breakEnd) : 0;

  return formatSecondsToDuration(totalSecondsClocks - totalSecondsBreaks);
};
